import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { OrganizationSchema } from "@/components/OrganizationSchema";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { ServicesProvider } from "@/contexts/ServicesContext";
import { PrivateRoute } from "@/components/admin/PrivateRoute";
import { TrackingScripts } from "@/components/TrackingScripts";
import { WhatsAppBubble } from "@/components/WhatsAppBubble";

// Public pages are lazy-loaded per-route so visitors only download the code for the
// page they're viewing. Admin pages are lazy-loaded as a group so public visitors
// never download the drag-and-drop editor / admin bundle at all.
const Home = lazy(() => import("@/pages/Home"));
const Services = lazy(() => import("@/pages/Services"));
const Marketing = lazy(() => import("@/pages/Marketing"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminPages = lazy(() => import("@/pages/admin/AdminPages"));
const AdminPageEditor = lazy(() => import("@/pages/admin/AdminPageEditor"));
const AdminServices = lazy(() => import("@/pages/admin/AdminServices"));
const AdminServiceEditor = lazy(() => import("@/pages/admin/AdminServiceEditor"));
const AdminPosts = lazy(() => import("@/pages/admin/AdminPosts"));
const AdminPostEditor = lazy(() => import("@/pages/admin/AdminPostEditor"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminHeader = lazy(() => import("@/pages/admin/AdminHeader"));
const AdminFooter = lazy(() => import("@/pages/admin/AdminFooter"));
const AdminTracking = lazy(() => import("@/pages/admin/AdminTracking"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));

const RouteFallback = () => <div className="min-h-screen" data-testid="route-loading-fallback" />;

const PublicLayout = ({ children }) => (
  <div className="App bg-white dark:bg-[#050506] min-h-screen">
    <TrackingScripts />
    <Navbar />
    <main>{children}</main>
    <Footer />
    <WhatsAppBubble />
  </div>
);

const AppRoutes = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <div className="dark">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<AdminPages />} />
              <Route path="pages/:slug" element={<AdminPageEditor />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="services/new" element={<AdminServiceEditor />} />
              <Route path="services/:slug" element={<AdminServiceEditor />} />
              <Route path="posts" element={<AdminPosts />} />
              <Route path="posts/new" element={<AdminPostEditor />} />
              <Route path="posts/:slug" element={<AdminPostEditor />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="tracking" element={<AdminTracking />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="header" element={<AdminHeader />} />
              <Route path="footer" element={<AdminFooter />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <SmoothScroll>
      <PublicLayout>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </Suspense>
      </PublicLayout>
    </SmoothScroll>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <SettingsProvider>
            <ServicesProvider>
              <OrganizationSchema />
              <BrowserRouter>
                <AppRoutes />
                <Toaster />
              </BrowserRouter>
            </ServicesProvider>
          </SettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
